import { initializeApp, getApps, type FirebaseApp } from 'firebase/app';
import {
  getAuth,
  GoogleAuthProvider,
  FacebookAuthProvider,
  signInWithPopup,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword as firebaseSignInEmail,
  sendEmailVerification,
  updateProfile,
  type ActionCodeSettings,
  type User as FirebaseUser,
} from 'firebase/auth';

// Support both Vite (VITE_) and Next.js (NEXT_PUBLIC_) env var prefixes
const getFirebaseConfig = () => ({
  apiKey:
    process.env.VITE_FIREBASE_API_KEY ||
    process.env.NEXT_PUBLIC_FIREBASE_API_KEY ||
    '',
  authDomain:
    process.env.VITE_FIREBASE_AUTH_DOMAIN ||
    process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN ||
    '',
  projectId:
    process.env.VITE_FIREBASE_PROJECT_ID ||
    process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID ||
    '',
});

const getFirebaseApp = (): FirebaseApp => {
  if (getApps().length > 0) {
    return getApps()[0];
  }
  return initializeApp(getFirebaseConfig());
};

export type SocialProviderKey = 'google' | 'facebook';

/**
 * Sign in with a social provider popup. Returns the Firebase ID token.
 * Caller is responsible for exchanging the token with the backend.
 */
export const getSocialIdToken = async (
  providerKey: SocialProviderKey
): Promise<string> => {
  const auth = getAuth(getFirebaseApp());

  if (providerKey === 'facebook') {
    const provider = new FacebookAuthProvider();
    provider.addScope('email');
    provider.addScope('public_profile');
    const result = await signInWithPopup(auth, provider);
    return result.user.getIdToken();
  }

  const provider = new GoogleAuthProvider();
  provider.addScope('email');
  provider.addScope('profile');
  provider.addScope('openid');
  const result = await signInWithPopup(auth, provider);
  return result.user.getIdToken();
};

/**
 * Send a passwordless email sign-in link to `email`.
 * The link will redirect to `redirectUrl` (e.g. `window.location.origin + '/email-callback'`).
 */
export const sendEmailSignInLink = async (
  email: string,
  redirectUrl: string
): Promise<void> => {
  const auth = getAuth(getFirebaseApp());
  const actionCodeSettings: ActionCodeSettings = {
    url: redirectUrl,
    handleCodeInApp: true,
  };
  await sendSignInLinkToEmail(auth, email, actionCodeSettings);
  // Persist email so the callback page can complete sign-in
  if (typeof window !== 'undefined') {
    window.localStorage.setItem('emailForSignIn', email);
  }
};

/**
 * Complete email link sign-in. Call this on the callback page.
 * Returns the Firebase ID token, or null if the link is not a valid email sign-in link.
 */
export const completeEmailSignIn = async (
  emailLink: string
): Promise<string | null> => {
  const auth = getAuth(getFirebaseApp());
  if (!isSignInWithEmailLink(auth, emailLink)) {
    return null;
  }
  let email =
    typeof window !== 'undefined'
      ? window.localStorage.getItem('emailForSignIn') || ''
      : '';
  if (!email) {
    email = window.prompt('Please provide your email for confirmation') || '';
  }
  const result = await signInWithEmailLink(auth, email, emailLink);
  if (typeof window !== 'undefined') {
    window.localStorage.removeItem('emailForSignIn');
  }
  return result.user.getIdToken();
};

/**
 * Thrown when a user tries to sign in with email/password but their email is not yet verified.
 */
export class EmailNotVerifiedError extends Error {
  public readonly firebaseUser: FirebaseUser;
  constructor(firebaseUser: FirebaseUser) {
    super('Please verify your email address before signing in.');
    this.name = 'EmailNotVerifiedError';
    this.firebaseUser = firebaseUser;
  }
}

/**
 * Create a new Firebase email/password user, set their display name, and send a verification email.
 * Does NOT create a backend account — that happens lazily on first verified sign-in.
 */
export const createFirebaseEmailUser = async (
  email: string,
  password: string,
  displayName: string
): Promise<void> => {
  const auth = getAuth(getFirebaseApp());
  const { user } = await createUserWithEmailAndPassword(auth, email, password);
  if (displayName) {
    await updateProfile(user, { displayName });
  }
  await sendEmailVerification(user);
};

/**
 * Sign in with email and password.
 * Throws `EmailNotVerifiedError` if the user's email is not yet verified.
 * Returns the Firebase ID token on success.
 */
export const signInWithEmailPassword = async (
  email: string,
  password: string
): Promise<string> => {
  const auth = getAuth(getFirebaseApp());
  const { user } = await firebaseSignInEmail(auth, email, password);
  if (!user.emailVerified) {
    throw new EmailNotVerifiedError(user);
  }
  return user.getIdToken();
};

/**
 * Re-send a verification email to the given Firebase user.
 */
export const resendVerificationEmail = async (
  user: FirebaseUser
): Promise<void> => {
  await sendEmailVerification(user);
};
